/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useState, useContext, useMemo, useEffect } from 'react';
import {
    CpdsInputFieldsWrapper,
    CpdsLabelWrapper,
    CpdsLabel,
    CpdsSelect,
    CpdsHelperTextWrapper,
    CpdsHelperLabel,
    CpdsSelectWrapper,
    Arrow,
} from '../styles/cpds-input-fields-style';
import { CpdsInputDropDownProps, BorderType } from '../typings';
import { Cpds } from '@Cpds.Ui';
import { ThemeContext } from 'styled-components';
import { themeToken } from '../../utils/cpds-utils';

const isHtml = (str: string): boolean => {
    const doc = new DOMParser().parseFromString(str, 'text/html');
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
};

const Dropdown: React.FC<CpdsInputDropDownProps> = ({
    inputId = "",
    domRef,
    exClasses = '',
    styleOverrides = {},
    inputWidth,
    isDisabled,
    LabelCopy,
    LabelIcon,
    LabelIconSize = { width: '10', height: '10' },
    DropdownOptions = [],
    HelperCopy = '',
    HelperIcon = 'information',
    HelperIconSize = { width: '20', height: '20' },
    isError = false,
    ErrorIcon = 'information',
    ErrorIconSize = { width: '20', height: '20' },
    ErrorMessage = '',
    isOverRideTypography = false,
    PlaceholderCopy = 'Select an option',  // Default placeholder
    getValue = () => { },
    onChange = () => { },
    SelectedValue = '',
    borderType = BorderType.RoundPill,
    ...rest
}) => {
    const { themeColorMode } = rest;
    const [isOpen, setIsOpen] = useState(false);
    const themeContext: any = useContext(ThemeContext);
    const cpds = themeContext?.styles?.cpds;
    const token = useMemo(() => themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode).token, [cpds, rest]);
    const [drpData, setDrpData] = useState(SelectedValue);

    const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);
    const handleClose = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        getValue(drpData); // Pass the current selected value to the getValue callback
    }, [drpData, getValue]);

    const onDropDownChange = useCallback(
        (e) => {
            setDrpData(e);
            onChange(e);
        },
        [onChange]
    );

    const getLabelCopy = useCallback(
        (LabelCopy) => {
            if (isHtml(LabelCopy)) {
                return (
                    <CpdsLabel
                        className="inputbox-top-label"
                        isDisabled={isDisabled}
                        themeColorMode={themeColorMode}
                        isOverRideTypography={isOverRideTypography}
                        dangerouslySetInnerHTML={{ __html: LabelCopy }}
                    ></CpdsLabel>
                );
            } else {
                return (
                    <CpdsLabel
                        className="inputbox-top-label"
                        isDisabled={isDisabled}
                        themeColorMode={themeColorMode}
                        isOverRideTypography={isOverRideTypography}
                    >
                        <p>{LabelCopy}</p>
                    </CpdsLabel>
                );
            }
        },
        [isDisabled, themeColorMode, isOverRideTypography]
    );

    return (
        <CpdsInputFieldsWrapper
            className={`cpds-input-fields ${exClasses} ${isError ? 'invalid' : ''}`}
            ref={domRef}
            style={styleOverrides}
            inputWidth={inputWidth}
        >
            {LabelCopy && (
                <CpdsLabelWrapper>
                    {getLabelCopy(LabelCopy)}
                    {LabelIcon && (
                        <Cpds.Icon
                            id="ico-1"
                            name={LabelIcon}
                            customSize={LabelIconSize}
                            color={
                                isDisabled
                                    ? token?.input_fields_label_disable_icon_color || '#31374F'
                                    : token?.input_fields_label_icon_color || '#31374F'
                            }
                        />
                    )}
                </CpdsLabelWrapper>
            )}
            <CpdsSelectWrapper className="text-field-wrapper" isDisabled={isDisabled} themeColorMode={themeColorMode}>
                <CpdsSelect
                    id={inputId}
                    isError={isError}
                    isOpen={isOpen}
                    onMouseDown={handleToggle}
                    onBlur={handleClose}
                    onChange={(e) => {
                        handleClose();
                        onDropDownChange(e);
                    }}
                    disabled={isDisabled}
                    value={drpData}
                    borderType={borderType}
                >
                    {PlaceholderCopy && <option value="" disabled hidden>{PlaceholderCopy}</option>}
                    {DropdownOptions?.map((option, index) => (
                        <option key={index} value={option?.value}>
                            {option?.label}
                        </option>
                    ))}
                </CpdsSelect>
                <Arrow isOpen={isOpen} />
            </CpdsSelectWrapper>
            {(HelperCopy || ErrorMessage) && (
                <CpdsHelperTextWrapper>
                    {(HelperIcon || ErrorIcon) && (
                        <Cpds.Icon
                            id="ico-3"
                            color={
                                isError
                                    ? token?.input_fields_error_label_color
                                    : isDisabled
                                        ? token?.input_fields_helper_icon_disable_color
                                        : token?.input_fields_helper_label_color
                            }
                            name={isError ? ErrorIcon : HelperIcon}
                            customSize={isError ? ErrorIconSize : HelperIconSize}
                        />
                    )}
                    <CpdsHelperLabel isError={isError} isDisabled={isDisabled} themeColorMode={themeColorMode} isOverRideTypography={isOverRideTypography}>
                        {isError ? ErrorMessage : HelperCopy}
                    </CpdsHelperLabel>
                </CpdsHelperTextWrapper>
            )}
        </CpdsInputFieldsWrapper>
    );
};

export default Dropdown;
